import { Component, Signal, HostBinding } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { WebsiteStateService } from '../../../state-management/services/website-state.service';
import { BusinessConfiguration } from '../../../state-management/model/interfaces/state.model';
import { AnalyticsService } from '../../../services/analytics.service';
import { ContactFormComponent } from '../contact-form/contact-form.component';
import { SocialMediaPlatform } from '../../../state-management/model/enums/state.enums';
import { IconService } from '../../../services/icon.service';

@Component({
  selector: 'app-quick-connect',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  templateUrl: './quick-connect.component.html',
  styleUrl: './quick-connect.component.scss'
})
export class QuickConnectComponent {
  public SocialMediaPlatform = SocialMediaPlatform;
  public socialMediaPlatforms: SocialMediaPlatform[] = Object.values(SocialMediaPlatform)
    .filter(value => typeof value === 'string' && value !== SocialMediaPlatform.WhatsApp) as SocialMediaPlatform[];

  public configuration: Signal<BusinessConfiguration> = this.websiteState.configuration;
  public isMinimized = false;
  public isVisible = true;

  @HostBinding('class') get hostClasses() {
    return `position-${this.widgetPosition}`;
  }

  constructor(
    private websiteState: WebsiteStateService,
    private analyticsService: AnalyticsService,
    private dialog: MatDialog,
    private router: Router,
    private iconService: IconService
  ) { }

  // Widget configuration getters
  get widgetConfig() {
    return this.configuration().widgets.quickActions;
  }

  get isWidgetEnabled(): boolean {
    return this.widgetConfig.isEnabled;
  }

  get widgetPosition(): string {
    return this.widgetConfig.position;
  }

  get enabledActions() {
    return this.widgetConfig.items
      .filter(item => item.isActive)
      .sort((a, b) => a.order - b.order);
  }

  // Check if specific action types are enabled
  isActionEnabled(actionType: string): boolean {
    return this.enabledActions.some(action => action.type === actionType);
  }

  // Override visibility based on widget configuration
  get shouldShowWidget(): boolean {
    return this.isVisible && this.isWidgetEnabled;
  }

  // Get custom label for action
  getActionLabel(actionType: string): string | undefined {
    const action = this.enabledActions.find(a => a.type === actionType);
    return action?.label;
  }

  // toggleMinimized(): void {
  //   this.isMinimized = !this.isMinimized;
  // }

  minimize(): void {
    this.isMinimized = true;
  }

  expand(): void {
    this.isMinimized = false;
  }

  makePhoneCall(): void {
    const phone = this.configuration().contactData.phone;
    if (phone) {
      window.location.href = `tel:${phone.replace(/\s/g, '')}`;
      this.analyticsService.trackContactIntent('phone', 'quick-connect-phone');
    }
  }

  sendEmail(): void {
    const email = this.configuration().contactData.email;
    if (email) {
      window.location.href = `mailto:${email}`;
      this.analyticsService.trackContactIntent('email', 'quick-connect-email');
    }
  }

  openWhatsApp(): void {
    // Get WhatsApp number from Social Media Links
    const whatsappLink = this.configuration().socialMediaLinks?.find(
      (link: any) => link.platform === SocialMediaPlatform.WhatsApp && link.isActive
    );

    if (whatsappLink?.url) {
      // Extract phone number from WhatsApp URL or use direct number
      let phone = whatsappLink.url;

      // If it's a WhatsApp URL, extract the number
      if (phone.includes('wa.me/')) {
        phone = phone.split('wa.me/')[1].split('?')[0];
      }

      // Remove all non-numeric characters
      const cleanPhone = phone.replace(/\D/g, '');

      // Add country code if needed
      const whatsappPhone = cleanPhone.startsWith('49') ? cleanPhone : `49${cleanPhone.startsWith('0') ? cleanPhone.substring(1) : cleanPhone}`;

      window.open(`https://wa.me/${whatsappPhone}`, '_blank');
      this.analyticsService.trackContactIntent('whatsapp', 'quick-connect-whatsapp');
    }
  }

  openDirections(): void {
    const contactData = this.configuration().contactData;
    if (contactData.street && contactData.city) {
      const address = `${contactData.street}, ${contactData.city}`;
      const encodedAddress = encodeURIComponent(address);
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, '_blank');
      this.analyticsService.trackContactIntent('directions', 'quick-connect-directions');
    }
  }

  getSocialMediaLink(platform: SocialMediaPlatform): string | null {
    const socialLinks = this.configuration().socialMediaLinks;
    const link = socialLinks.find(l => l.platform === platform && l.isActive);
    return link ? link.url : null;
  }

  openSocialMedia(platform: SocialMediaPlatform): void {
    const url = this.getSocialMediaLink(platform);
    if (url) {
      window.open(url, '_blank');
      this.analyticsService.trackClick(`quick_connect_${platform.toLowerCase()}`)
    };
  }

  openOrderForm(): void {
    this.router.navigate(['/kontakt'], {
      queryParams: { intent: 'order' }
    });
    this.analyticsService.trackClick('quick_connect_order_form');
  }

  openContactForm(): void {
    const dialogRef = this.dialog.open(ContactFormComponent, {
      width: '90vw',
      maxWidth: '600px',
      maxHeight: '90vh',
      panelClass: 'contact-form-dialog'
    });
    this.analyticsService.trackClick('quick_connect_contact_form');
  }

  // Helper methods for template
  public hasPhone(): boolean {
    const contactData = this.configuration().contactData;
    return !!(contactData.phone || contactData.mobile);
  }

  public hasEmail(): boolean {
    return !!this.configuration().contactData.email;
  }

  public hasWhatsApp(): boolean {
    return !!this.configuration().socialMediaLinks?.find(
      (link: any) => link.platform === 'whatsapp' && link.isActive && link.url
    );
  }

  public hasAddress(): boolean {
    return !!(this.configuration().contactData.street && this.configuration().contactData.city);
  }

  public hasAnySocialMedia(): boolean {
    return this.socialMediaPlatforms.some(platform => this.hasSocialMedia(platform));
  }

  public hasSocialMedia(platform: SocialMediaPlatform): boolean {
    return !!this.getSocialMediaLink(platform);
  }

}
